import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store'
import { CreditCalculationStructure, GseStructure } from './../../model/credit-structure.model'
import {
  GseStructureGroup,
  GseStructureGroupEntity,
  GseStructureGroupMember,
  GseStructureGroupMemberEntity,
} from '../../model/gse-structure-group.model'
import { ProgramsYearsFilter } from './../../../tier/credit/credit-tier.model'
import { ApplicationError } from '../../../error/model/error'

export const GseStructureGroupFilterActions = createActionGroup({
  source: 'GSE Group Filters',
  events: {
    Update: props<{ filters: ProgramsYearsFilter }>(),
    'Fetch Structures': props<{ filters: ProgramsYearsFilter }>(),
    'Fetch Structures Success': props<{
      gseStructures: GseStructure[]
    }>(),
    'Fetch Structures Failure': props<{ error: ApplicationError }>(),
    'Fetch Groups': props<{ filters: ProgramsYearsFilter }>(),
    'Fetch Groups Success': props<{
      gseStructureGroups: GseStructureGroup[]
    }>(),
    'Fetch Groups Failure': props<{ error: ApplicationError }>(),
    Reset: emptyProps(),
  },
})

enum GseStructureGroupActionTypes {
  AddGseStructureGroup = '[GSE Group] Add GSE Structure Group',
  AddGseStructureGroupSuccess = '[GSE Group] Add GSE Structure Group - Success',
  AddGseStructureGroupFailure = '[GSE Group] Add GSE Structure Group - Failure',
  UpdateGseStructureGroup = '[GSE Group] Update GSE Structure Group',
  UpdateGseStructureGroupSuccess = '[GSE Group] Update GSE Structure Group - Success',
  UpdateGseStructureGroupFailure = '[GSE Group] Update GSE Structure Group - Failure',
  RenameGseStructureGroup = '[GSE Group] Rename GSE Structure Group',
  RemoveGseStructureGroup = '[GSE Group] Remove GSE Structure Group',
  DeleteGseStructureGroup = '[GSE Group] Delete GSE Structure Group',
  DeleteGseStructureGroupSuccess = '[GSE Group] Delete GSE Structure Group - Success',
  DeleteGseStructureGroupFailure = '[GSE Group] Delete GSE Structure Group - Failure',
  SaveGseGroups = '[GSE Group] Save Groups',
  SaveGseStructureGroup = '[GSE Group] Save GSE Structure Groups',
  SaveGseStructureGroupSuccess = '[GSE Group] Save GSE Structure Group - Success',
  SaveGseStructureGroupFailure = '[GSE Group] Save GSE Structure Group - Failure',
  FetchGseStructureGroupsForProgram = '[GSE Group] Fetch GSE Structure Groups for Selected Program',
  FetchGseStructureGroupsForProgramSuccess = '[GSE Group] Fetch GSE Structure Groups for Selected Program - Success',
  FetchGseStructureGroupsForProgramFailure = '[GSE Group] Fetch GSE Structure Groups for Selected Program - Failure',
  FetchGseStructureGroupDetails = '[GSE Group] Fetch GSE Structure Group Details for Selected Group',
  FetchGseCalculationStructureForGroup = '[GSE Group] Fetch GSE Calculation Structure for Selected Group',
  FetchGseCalculationStructureForGroupSuccess = '[GSE Group] Fetch GSE Calculation Structure for Selected Group - Success',
  FetchGseCalculationStructureForGroupFailure = '[GSE Group] Fetch GSE Calculation Structure for Selected Group - Failure',
}

enum GseStructureGroupMemberActionTypes {
  AddGseStructureToGroup = '[GSE Group Members] Add GSE Structure to Structure Group',
  RemoveGseStructureFromGroup = '[GSE Group Members] Remove GSE Structure from Structure Group',
  SaveGseStructureGroupMembersSuccess = '[GSE Group Members] Save GSE Structure Group Members - Success',
  SaveGseStructureGroupMembersFailure = '[GSE Group Members] Save GSE Structure Group Members - Failure',
  FetchGseGroupMembers = '[GSE Group Members] Fetch GSE Group Members for Selected Group',
  FetchGseGroupMembersSuccess = '[GSE Group Members] Fetch GSE Group Members for Selected Group - Success',
  FetchGseGroupMembersFailure = '[GSE Group Members] Fetch GSE Group Members for Selected Group - Failure',
  FetchGseGroupMemberCalculationStructuresSuccess = '[GSE Group Members] Fetch GSE Group Member Calculation Structures - Success',
  FetchGseGroupMemberCalculationStructuresFailure = '[GSE Group Members] Fetch GSE Group Member Calculation Structures - Failure',
}

/* Group Actions */
export const AddGseStructureGroup = createAction(
  GseStructureGroupActionTypes.AddGseStructureGroup,
  props<{ gseStructure: GseStructure }>(),
)
export const AddGseStructureGroupFailure = createAction(
  GseStructureGroupActionTypes.AddGseStructureGroupFailure,
  props<{ error: ApplicationError }>(),
)
export const AddGseStructureGroupSuccess = createAction(
  GseStructureGroupActionTypes.AddGseStructureGroupSuccess,
  props<{
    gseStructureGroupEntity: GseStructureGroupEntity
    gseStructureGroupMemberEntity: GseStructureGroupMemberEntity
  }>(),
)
export const UpdateGseStructureGroup = createAction(
  GseStructureGroupActionTypes.UpdateGseStructureGroup,
  props<{ gseStructure: GseStructure }>(),
)
export const UpdateGseStructureGroupFailure = createAction(
  GseStructureGroupActionTypes.UpdateGseStructureGroupFailure,
  props<{ error: ApplicationError }>(),
)
export const UpdateGseStructureGroupSuccess = createAction(
  GseStructureGroupActionTypes.UpdateGseStructureGroupSuccess,
  props<{
    gseStructureGroupMemberEntity: GseStructureGroupMemberEntity
  }>(),
)
export const RenameGseStructureGroup = createAction(
  GseStructureGroupActionTypes.RenameGseStructureGroup,
  props<{ label: string }>(),
)
export const RemoveGseStructureGroup = createAction(
  GseStructureGroupActionTypes.RemoveGseStructureGroup,
)
export const DeleteGseStructureGroup = createAction(
  GseStructureGroupActionTypes.DeleteGseStructureGroup,
)
export const DeleteGseStructureGroupFailure = createAction(
  GseStructureGroupActionTypes.DeleteGseStructureGroupFailure,
  props<{ error: ApplicationError }>(),
)
export const DeleteGseStructureGroupSuccess = createAction(
  GseStructureGroupActionTypes.DeleteGseStructureGroupSuccess,
  props<{ gseStructureGroup: GseStructureGroup }>(),
)
export const SaveGseGroups = createAction(GseStructureGroupActionTypes.SaveGseGroups)
export const SaveGseStructureGroup = createAction(
  GseStructureGroupActionTypes.SaveGseStructureGroup,
  props<{ gseStructureGroupEntity: GseStructureGroupEntity }>(),
)
export const SaveGseStructureGroupFailure = createAction(
  GseStructureGroupActionTypes.SaveGseStructureGroupFailure,
  props<{ error: ApplicationError }>(),
)
export const SaveGseStructureGroupSuccess = createAction(
  GseStructureGroupActionTypes.SaveGseStructureGroupSuccess,
  props<{
    gseStructureGroupEntity: GseStructureGroupEntity
    groupMembers: GseStructureGroupMemberEntity[]
    removedMemberIds: number[]
  }>(),
)
export const FetchGseStructureGroupsForProgram = createAction(
  GseStructureGroupActionTypes.FetchGseStructureGroupsForProgram,
  props<{ programId: string }>(),
)
export const FetchGseStructureGroupsForProgramFailure = createAction(
  GseStructureGroupActionTypes.FetchGseStructureGroupsForProgramFailure,
  props<{ error: ApplicationError }>(),
)
export const FetchGseStructureGroupsForProgramSuccess = createAction(
  GseStructureGroupActionTypes.FetchGseStructureGroupsForProgramSuccess,
  props<{ gseStructureGroups: GseStructureGroup[] }>(),
)
export const FetchGseStructureGroupDetails = createAction(
  GseStructureGroupActionTypes.FetchGseStructureGroupDetails,
  props<{ gseStructureGroup: GseStructureGroup }>(),
)
export const FetchGseCalculationStructureForGroup = createAction(
  GseStructureGroupActionTypes.FetchGseCalculationStructureForGroup,
  props<{ gseStructureGroup: GseStructureGroup }>(),
)
export const FetchGseCalculationStructureForGroupFailure = createAction(
  GseStructureGroupActionTypes.FetchGseCalculationStructureForGroupFailure,
  props<{ error: ApplicationError }>(),
)
export const FetchGseCalculationStructureForGroupSuccess = createAction(
  GseStructureGroupActionTypes.FetchGseCalculationStructureForGroupSuccess,
  props<{
    gseStructureGroup: GseStructureGroup
    creditCalculationStructure: CreditCalculationStructure
  }>(),
)

/* Group Member Actions */
export const AddGseStructureToGroup = createAction(
  GseStructureGroupMemberActionTypes.AddGseStructureToGroup,
  props<{ gseStructure: GseStructure }>(),
)
export const RemoveGseStructureFromGroup = createAction(
  GseStructureGroupMemberActionTypes.RemoveGseStructureFromGroup,
  props<{
    gseStructure: GseStructure
    creditCalculationStructure: CreditCalculationStructure
    gseStructureGroupMemberId?: number
  }>(),
)
export const SaveGseStructureGroupMembersFailure = createAction(
  GseStructureGroupMemberActionTypes.SaveGseStructureGroupMembersFailure,
  props<{ error: ApplicationError }>(),
)
export const SaveGseStructureGroupMembersSuccess = createAction(
  GseStructureGroupMemberActionTypes.SaveGseStructureGroupMembersSuccess,
  props<{ gseStructureGroupMembers: GseStructureGroupMember[] }>(),
)
export const FetchGseGroupMembers = createAction(
  GseStructureGroupMemberActionTypes.FetchGseGroupMembers,
  props<{ gseStructureGroup: GseStructureGroup }>(),
)
export const FetchGseGroupMembersFailure = createAction(
  GseStructureGroupMemberActionTypes.FetchGseGroupMembersFailure,
  props<{ error: ApplicationError }>(),
)
export const FetchGseGroupMembersSuccess = createAction(
  GseStructureGroupMemberActionTypes.FetchGseGroupMembersSuccess,
  props<{
    gseStructureGroupMembers: GseStructureGroupMember[]
    gseStructureGroup: GseStructureGroup
  }>(),
)
export const FetchGseGroupMemberCalculationStructuresFailure = createAction(
  GseStructureGroupMemberActionTypes.FetchGseGroupMemberCalculationStructuresFailure,
  props<{ error: ApplicationError }>(),
)
export const FetchGseGroupMemberCalculationStructuresSuccess = createAction(
  GseStructureGroupMemberActionTypes.FetchGseGroupMemberCalculationStructuresSuccess,
  props<{
    gseStructureGroupMembers: GseStructureGroupMember[]
    gseStructures: GseStructure[]
    creditCalculationStructures: CreditCalculationStructure[]
  }>(),
)

export const ToggleGseStructureGroupChecked = createAction(
  '[GSE Group] Toggle Gse Structure Group Checked',
  props<{ gseStructureGroup: GseStructureGroup }>(),
)
